import {keys} from 'ts-transformer-keys';

import LocalizableError from '~/shared/src/base/LocalizableError';
import StackError from '~/shared/src/base/StackError';
import {BaseRequestParameters} from '~/shared/src/model/BaseRequest';
import {LoadResult} from '~/shared/src/utilities/modules/queryExecute';

type RequiredKeys<T> = { [K in keyof T]-? : {} extends Pick<T, K> ? never : K }[keyof T];
type RequiredOf<T> = Pick<T, RequiredKeys<T>>;


/*** MetaModel types *************************************************************************************************/

// CFG:AiCompletionParameters
export type AiCompletionParameters = {
    id : number;
    key : string;
    name? : string;

    maxTokens : number;
    temperature : number;
};
export const requiredAiCompletionParametersProps = keys<RequiredOf<AiCompletionParameters>>();
export const aiCompletionParametersProps = keys<AiCompletionParameters>();

// CFG:AiModelConfig
export type AiModelConfig = {
    id : number;
    key : string;
    name? : string;

    apiKey : string;
    baseUrl : string;
    headers? : { [ header : string ] : string };
    model : string;
    order : number;

    completionParameters? : AiCompletionParameters;
};
export const requiredAiModelConfigProps = keys<RequiredOf<AiModelConfig>>();
export const aiModelConfigProps = keys<AiModelConfig>();

// CFG:AiChatCompletion
export type AiChatCompletion = {
    id : number;
    key : string;
    name? : string;

    system : string;
    modelConfig : AiModelConfig;
    completionParameters? : AiCompletionParameters;
    responseJsonFormat? : Record<string, any>;
};
export const requiredAiChatCompletionProps = keys<RequiredOf<AiChatCompletion>>();
export const aiChatCompletionProps = keys<AiChatCompletion>();

/*** method types ****************************************************************************************************/

/*** chat ***/
export type ChatParameters = {
    aiChatCompletionKey : string;
    question : string;
    fileData? : Buffer | string;
    fileMimeType? : string;
};
export type ChatResult = {
    success : boolean;
    data : {
        answer : string;
    };
    metaData : {
        metrics : Metric[];
    };
    error? : Error | LocalizableError | StackError | string
};

/*** generateCypher ***/
export type GenerateCypherParameters = Partial<BaseRequestParameters> & {
    aiChatCompletionKey : string;
    question : string;
};
export type GenerateCypherResult = {
    success : boolean;
    data : {
        cypher : string;
    };
    metaData : {
        metrics : Metric[];
    };
    error? : Error | LocalizableError | StackError | string
};


/*** generateCypherAndExecuteQuery ***/
export type GenerateCypherAndExecuteQueryParameters = Partial<BaseRequestParameters> & {
    aiChatCompletionKey : string;
    question : string;
};
export type GenerateCypherAndExecuteQueryResult = {
    success : boolean;
    data : {
        cypher : string;
    } & LoadResult['data'];
    metaData : {
        metrics : Metric[];
    } & LoadResult['metaData'];
    error? : Error | LocalizableError | StackError | string
};

/*** isAvailable ***/
export type IsAvailableParameters = {
    aiChatCompletionKey : string;
};
export type IsAvailableResult = {
    success : boolean;
    data : {
        available : boolean;
    };
    metaData : {
        metrics : Metric[];
    };
    error? : Error | LocalizableError | StackError | string
};
